/**
 * RevealConfirmDialog — the typed-key guard for the two irreversible-in-effect
 * dimension changes.
 *
 * Its original and default use is the DECISION 7.4 reveal guard (§4): turning a
 * dimension's `is_internal_only` OFF makes a previously hidden personality
 * dimension visible, which cannot be undone in effect (members / downstream may
 * have already seen it), so it demands more friction than a one-click toggle.
 * This is the QuillActionConfirm visual language (burgundy warning header,
 * Playfair title, Cancel/Confirm footer) with a required typed-name field
 * bolted on: the confirm button stays disabled until the admin types the exact
 * `dimension_key`, which the page then sends to the backend as `confirm_reveal`
 * (or `confirm_charter_sensitive`). A reason is also required for the change log.
 *
 * THE COPY IS OVERRIDABLE, THE MECHANISM IS NOT. Clearing
 * `is_charter_sensitive` is a different sentence about a different column, but
 * it is the same act — it takes a protection away from a bereaved family, from
 * the next retrieval onwards — so it gets this dialog with its own words rather
 * than a fifth near-identical copy of it (the mistake QuillActionConfirm exists
 * to have fixed). Omit the copy props and it is the 7.4 reveal, unchanged.
 *
 * The SAFE direction of both switches — hiding a dimension, adding the
 * charter-sensitive gate — is one click via the plain QuillActionConfirm. See
 * DimensionsPage.
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { colors, typography } from '../../../theme/designTokens';

export interface RevealConfirmDialogProps {
  open: boolean;
  dimensionKey: string;
  displayName: string;
  loading?: boolean;
  /** Dialog heading. Defaults to the 7.4 reveal. */
  title?: string;
  /**
   * The consequence sentence, rendered above the typed-key field. Defaults to
   * the 7.4 reveal's. Takes a node so the dimension's name can be emphasised.
   */
  body?: React.ReactNode;
  /** Confirm-button label, and its label while the write is in flight. */
  confirmLabel?: string;
  pendingLabel?: string;
  /** Label + aria-label of the required reason field. */
  reasonLabel?: string;
  /** Called with the typed reason once the typed name matches. */
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

const RevealConfirmDialog: React.FC<RevealConfirmDialogProps> = ({
  open,
  dimensionKey,
  displayName,
  loading = false,
  title = 'Reveal internal-only dimension',
  body,
  confirmLabel = 'Reveal dimension',
  pendingLabel = 'Revealing…',
  reasonLabel = 'Reason for reveal',
  onConfirm,
  onCancel,
}) => {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');

  // Reset the fields whenever the dialog (re)opens for a dimension.
  useEffect(() => {
    if (open) {
      setTyped('');
      setReason('');
    }
  }, [open, dimensionKey]);

  const nameMatches = typed.trim() === dimensionKey;
  const reasonValid = reason.trim().length > 0;
  const canConfirm = nameMatches && reasonValid && !loading;

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontFamily: typography.fonts.display,
          color: colors.error.dark,
        }}
      >
        <WarningAmberIcon sx={{ color: colors.error.main }} />
        {title}
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body1" sx={{ lineHeight: 1.6, fontWeight: 500 }}>
          {body ?? (
            <>
              You are about to turn <strong>{displayName}</strong> from internal-only to visible.
              This exposes a previously hidden personality dimension and applies to the live Quill
              engine (production). To confirm, type the dimension key exactly.
            </>
          )}
        </Typography>

        <Box
          sx={{
            mt: 2,
            p: 1.25,
            borderRadius: '8px',
            backgroundColor: colors.gray[100],
            fontFamily: typography.fonts.mono,
            fontWeight: 700,
            textAlign: 'center',
            userSelect: 'all',
          }}
        >
          {dimensionKey}
        </Box>

        <TextField
          label="Type the dimension key to confirm"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          fullWidth
          size="small"
          autoComplete="off"
          sx={{ mt: 2 }}
          error={typed.length > 0 && !nameMatches}
          helperText={typed.length > 0 && !nameMatches ? 'The typed key does not match.' : ' '}
          inputProps={{ 'aria-label': 'Type the dimension key to confirm' }}
        />

        <TextField
          label={reasonLabel}
          placeholder="Recorded in the change log (required)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
          error={reason.length > 0 && !reasonValid}
          inputProps={{ 'aria-label': reasonLabel }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(reason.trim())}
          disabled={!canConfirm}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{
            backgroundColor: colors.error.main,
            color: '#FFFFFF',
            '&:hover': { backgroundColor: colors.error.dark },
          }}
        >
          {loading ? pendingLabel : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RevealConfirmDialog;
